import { useEffect, useState } from "react";

import { inputGlowWrap, inputGlowInner } from "@/lib/card-styles";
import { getKBTypes, type KBZoneSummary } from "@/lib/api/agent";

// ── Interfaces ────────────────────────────────────────────────────────────────

interface ZoneFormData {
  name: string;
  floor: number;
  zone_type: string | null;
  description: string;
}

export interface ZoneFormProps {
  on_submit: (data: ZoneFormData) => Promise<void>;
  on_cancel: () => void;
}

// ── ZoneForm ──────────────────────────────────────────────────────────────────

export function ZoneForm({ on_submit, on_cancel }: ZoneFormProps) {
  const [name, set_name] = useState("");
  const [floor, set_floor] = useState(1);
  const [zone_type, set_zone_type] = useState<string>("");
  const [description, set_description] = useState("");
  const [submitting, set_submitting] = useState(false);
  const [kb_zone_types, set_kb_zone_types] = useState<KBZoneSummary[]>([]);

  useEffect(() => {
    getKBTypes()
      .then((r) => set_kb_zone_types(r.zone_types))
      .catch(() => {});
  }, []);

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    set_submitting(true);
    try {
      await on_submit({
        name: name.trim(),
        floor,
        zone_type: zone_type || null,
        description: description.trim(),
      });
    } finally {
      set_submitting(false);
    }
  }

  return (
    <form
      onSubmit={(e) => void handle_submit(e)}
      className="rounded-lg border border-primary/40 bg-card p-4"
    >
      <p className="mb-3 text-sm font-medium">Uusi vyöhyke</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs text-muted-foreground">
            Nimi
          </label>
          <div className={inputGlowWrap}>
            <div className={inputGlowInner}>
              <input
                autoFocus
                value={name}
                onChange={(e) => set_name(e.target.value)}
                placeholder="esim. Toimisto A"
                className="w-full bg-transparent px-2 py-1.5 text-sm placeholder:text-muted-foreground outline-none"
              />
            </div>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            Kerros
          </label>
          <div className={inputGlowWrap}>
            <div className={inputGlowInner}>
              <input
                type="number"
                value={floor}
                onChange={(e) => set_floor(Number(e.target.value))}
                className="w-full bg-transparent px-2 py-1.5 text-sm outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            Vyöhyketyyppi
          </label>
          <select
            value={zone_type}
            onChange={(e) => set_zone_type(e.target.value)}
            className="w-full rounded border border-border/40 bg-background px-2 py-1.5 text-sm outline-none focus:border-primary/60"
          >
            <option value="">— Ei tyyppiä —</option>
            {kb_zone_types.map((zt) => (
              <option key={zt.id} value={zt.id}>
                {zt.icon} {zt.name_fi}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-3">
        <label className="mb-1 block text-xs text-muted-foreground">
          Kuvaus (valinnainen)
        </label>
        <div className={inputGlowWrap}>
          <div className={inputGlowInner}>
            <input
              value={description}
              onChange={(e) => set_description(e.target.value)}
              placeholder="Lyhyt kuvaus vyöhykkeestä"
              className="w-full bg-transparent px-2 py-1.5 text-sm placeholder:text-muted-foreground outline-none"
            />
          </div>
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={submitting || !name.trim()}
          className="rounded bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50"
        >
          {submitting ? "Tallennetaan..." : "Lisää"}
        </button>
        <button
          type="button"
          onClick={on_cancel}
          className="rounded px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          Peruuta
        </button>
      </div>
    </form>
  );
}
